import { Typography, Card, CardContent, CardActionArea, Divider, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { User } from "../models";
import React from "react";
import { UserContext } from "../hooks/UserContext";
import { NavLink, USER_LINKS, ADMIN_LINKS, DEVELOPER_LINKS } from "./Toolbar";
import { API } from "../api";

interface LandingPageProps {
	setLoggedIn: (user: User) => void;
}

const ISSUE_COUNT_FETCHERS: Record<string, () => Promise<unknown[]>> = {
	"/admin/unmatched-deals": () => API.Data.Jobs.issues.getUnmatchedDealJobs(),
	"/admin/agrian-issues": () => API.Data.Jobs.issues.getAgrian(),
	"/admin/post-sampling-issues": () => API.Data.Jobs.issues.getPostSamplingStuckJobs(),
};

// oxlint-disable-next-line no-unused-vars
export default function HomePage(props: LandingPageProps) {
	const navigate = useNavigate();
	const user = React.useContext(UserContext) as User | undefined;
	const [issueCounts, setIssueCounts] = React.useState<Record<string, number>>({});
	const [loadingCounts, setLoadingCounts] = React.useState(false);

	React.useEffect(() => {
		if (!user?.isDeveloper) return;
		setLoadingCounts(true);
		const entries = Object.entries(ISSUE_COUNT_FETCHERS);
		Promise.allSettled(entries.map(([, fetch]) => fetch())).then((results) => {
			const counts: Record<string, number> = {};
			results.forEach((result, i) => {
				if (result.status === "fulfilled") {
					counts[entries[i][0]] = result.value.length;
				}
			});
			setIssueCounts(counts);
			setLoadingCounts(false);
		});
	}, [user?.isDeveloper]);

	function linkCard(link: NavLink) {
		const count = issueCounts[link.to];
		const showCount = user?.isDeveloper && link.to in ISSUE_COUNT_FETCHERS;

		return (
			<Grid key={link.to} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
				<Card className="home-card">
					<CardActionArea onClick={() => navigate(link.to)}>
						<CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
							{link.icon}
							<Typography variant="h6">{link.text}</Typography>
							{showCount && (
								<>
									<span style={{ flex: 1 }} />
									{loadingCounts || count === undefined ? (
										<CircularProgress size={18} />
									) : (
										<Typography variant="h6" color={count > 0 ? "error" : "success"}>
											{count}
										</Typography>
									)}
								</>
							)}
						</CardContent>
					</CardActionArea>
				</Card>
			</Grid>
		);
	}

	// Skip the "Home" link on the home page itself
	const pageLinks = USER_LINKS.filter((link) => link.to !== "/home");

	return (
		<>
			<Grid container spacing={2} className="home-grid">
				{pageLinks.map(linkCard)}
			</Grid>
			{user?.isAdmin && (
				<>
					<Divider sx={{ mx: 2, mb: 2, mt: 2 }} />
					<Grid container spacing={2} className="home-grid">
						{ADMIN_LINKS.map(linkCard)}
					</Grid>
				</>
			)}
			{user?.isDeveloper && (
				<>
					<Divider sx={{ mx: 2, mb: 2, mt: 2 }} />
					<Grid container spacing={2} className="home-grid">
						{DEVELOPER_LINKS.map(linkCard)}
					</Grid>
				</>
			)}
		</>
	);
}
